import {
  Component,
  OnInit,
  ViewChild,
  Inject,
  DestroyRef,
  inject
} from '@angular/core';
import { DOCUMENT, CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import {
  GridsterConfig,
  GridsterItemComponent,
  GridsterComponent,
  GridType,
  CompactType
} from 'angular-gridster2';

import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, map, distinctUntilChanged, exhaustMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';

import { DashboardRefreshService } from './dashboard-refresh.service';
import { DashboardItem, DashboardLayout } from './dashboard.types';
import { DashboardLayoutService } from './dashboard-layout.service';
import { DashboardRegistry } from './dashboard-registry';
import { DashboardWidgetsService } from './dashboard-widgets.service';
import { GridItemComponent } from './grid-item.component';

import { RmsModule } from '@rms';
import { RmsPromptComponent } from '../../custom-modules/rms-module/rms-prompt/rms-prompt.component';

import { WebService, lastValueFrom, RealtimeService } from '@services';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  imports: [
    RmsPromptComponent,
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    GridsterComponent,
    GridsterItemComponent,
    GridItemComponent,
    RmsModule
  ]
})
export class DashboardComponent implements OnInit {
  @ViewChild('prompt') prompt!: RmsPromptComponent;

  private destroyRef = inject(DestroyRef);

  formResponses: any[] = [];
  editDashboard = false;
  options!: GridsterConfig;
  dashboard: DashboardItem[] = [];
  widgets: any = [];
  selectedLayout: any | null = null;
  availableLayouts: any[] = [];
  loaded = false;

  private persistTimer!: any;

  // Only bind realtime once
  private realtimeBound = false;

  // Optional safety: prevent posting the same payload twice
  private processedKeys = new Set<string>();

  readonly userId = localStorage.getItem('email') || 'current-user';
  layoutError = '';
  readonly UserName = (localStorage.getItem('email') || '').split('@')[0];

  constructor(
    private layoutSvc: DashboardLayoutService,
    private registry: DashboardRegistry,
    private webService: WebService,
    private widgetService: DashboardWidgetsService,
    private realtime: RealtimeService,
    private dashboardRefreshService: DashboardRefreshService,
    @Inject(DOCUMENT) private doc: Document
  ) {
    this.widgets = this.widgetService.GetAllWidgets();
    this.destroyRef.onDestroy(() => clearTimeout(this.persistTimer));
  }

  async ngOnInit() {
    // Load layout/UI state (safe to call multiple times)
    await this.loadLayoutOnly();

    // Bind sockets/subscriptions ONCE (this prevents double POSTs)
    this.bindRealtimeOnce();
  }

  /**
   * Loads layouts + initializes grid/options.
   * SAFE to call repeatedly (e.g., after SaveCustomLayout/UpdateLayout).
   * Does NOT create subscriptions.
   */
  private async loadLayoutOnly() {
    let saved = this.layoutSvc.load(this.userId);

    this.layoutError='';
    try { this.availableLayouts = await this.getPresetLayouts(this.UserName); }
    catch { this.availableLayouts=[];this.layoutError='Saved layouts could not be loaded. Your local workspace is still available.'; }
    this.selectedLayout=saved?.selectedLayout || {UserName:this.UserName,DefaultConfig:false};
    if (!saved) {
      const defaultLayout=this.availableLayouts.find(l=>l.DefaultConfig);
      if(defaultLayout){try {saved=JSON.parse(defaultLayout.DashboardConfig);this.selectedLayout=defaultLayout;}catch {this.layoutError='The default layout could not be read. Choose another layout.';}}
    }
    this.dashboard=Array.isArray(saved?.items)?saved.items:[];
    this.initOptions();
    this.loaded = true;
  }

  /**
   * Subscribes to realtime streams ONCE.
   * Uses takeUntilDestroyed so it is automatically cleaned up when component is destroyed. [1](https://angular.dev/ecosystem/rxjs-interop/take-until-destroyed)[2](https://stackoverflow.com/questions/76264067/takeuntildestroyed-can-only-be-used-within-an-injection-context)
   */
  private bindRealtimeOnce() {
    if (this.realtimeBound) return;
    this.realtimeBound = true;

    this.realtime.formResponses$
      .pipe(
        takeUntilDestroyed(this.destroyRef),


        // normalize/clean keys once
        map(res => {
          const cleaned: any = {};
          Object.keys(res).forEach(key => {
            const cleanKey = key.replace(/\s+/g, '');
            cleaned[cleanKey] = res[key];
          });

          // create a stable idempotency key so identical payload won't POST twice
          const key = this.stableStringify(cleaned);
          return { cleaned, key };
        }),

        // drop repeats (client-side idempotency)
        distinctUntilChanged((a, b) => a.key === b.key),

        // if a form comes in while one is processing, ignore until finished
        exhaustMap(({ cleaned, key }) => {
          if (this.processedKeys.has(key)) {
            return of(null);
          }
          this.processedKeys.add(key);

          return this.webService.getWebCall('venders', 'create_supplier_request', {
            body: [{ value: cleaned, name: 'supplierJson' }],
            skipLoader: true
          }).pipe(
            tap(() => this.dashboardRefreshService.triggerSupplierRequestsRefresh())
          );
        })
      )
      .subscribe({
        next: (result) => {
          if (result) console.log('[dashboard] create_supplier_request ok');
        },
        error: (err) => {
          console.error('[dashboard] create_supplier_request failed', err);
        }
      });
  }

  /**
   * Stable stringify: sorts keys so order doesn't change the idempotency key
   */
  private stableStringify(obj: Record<string, any>): string {
    const keys = Object.keys(obj).sort();
    const sorted: any = {};
    for (const k of keys) sorted[k] = obj[k];
    return JSON.stringify(sorted);
  }

  private initOptions() {
    this.options = {
      gridType: GridType.VerticalFixed,
      mobileBreakpoint: 900,
      keepFixedHeightInMobile: true,
      fixedRowHeight: 100,
      compactType: CompactType.None,
      pushItems: true,
      pushDirections: { north: true, south: true, east: true, west: true },
      pushResizeItems: true,
      draggable: {
        enabled: this.editDashboard,
        dragHandleClass: 'drag-handle',
        ignoreContentClass: 'no-drag'
      },
      resizable: { enabled: this.editDashboard },
      swap: true,
      margin: 10,
      outerMargin: true,
      minCols: 15,
      maxCols: 15,
      minRows: 8,
      maxRows: 12,
      displayGrid: this.editDashboard ? 'always' : 'none',
      disableScrollHorizontal: true,
      itemChangeCallback: () => this.persistDebounced(),
      itemResizeCallback: () => this.persistDebounced(),
      minItemArea: 1,
      itemResizeStop: () => this.persist()
    };
  }

  useDailyLayout() {
    this.dashboard=[
      {id:crypto.randomUUID(),type:'recent-purchases',x:0,y:0,cols:5,rows:5},
      {id:crypto.randomUUID(),type:'inbound-attention',x:5,y:0,cols:5,rows:5},
      {id:crypto.randomUUID(),type:'arriving-week',x:10,y:0,cols:5,rows:5},
      {id:crypto.randomUUID(),type:'supplier_request',x:0,y:5,cols:7,rows:4}
    ];
    this.selectedLayout={UserName:this.UserName,LayoutName:'Daily overview',DefaultConfig:false};
    this.persist();
  }

  toggleEdit() {
    this.editDashboard = !this.editDashboard;
    if (this.options?.api?.optionsChanged) {
      this.options.draggable = { enabled: this.editDashboard, dragHandleClass: 'drag-handle' };
      this.options.resizable = { enabled: this.editDashboard };
      this.options.displayGrid = this.editDashboard ? 'always' : 'none';
      this.options.api.optionsChanged();
    }
  }

  addWidget(type: string, size: 'sm' | 'md' | 'lg' = 'md', where: 'top' | 'auto' = 'auto') {
    const id = crypto.randomUUID?.() ?? Math.random().toString(36).slice(2);
    const { cols, rows } =
      size === 'sm' ? { cols: 4, rows: 4 } :
        size === 'lg' ? { cols: 6, rows: 6 } :
          { cols: 5, rows: 4 };

    const item: DashboardItem = { id, type, x: 0, y: 0, cols, rows };

    if (where === 'top') {
      const prevPush = this.options.pushItems;
      const prevSwap = this.options.swap;
      this.options.pushItems = false;
      this.options.swap = false;
      this.options.api?.optionsChanged?.();

      this.bumpDown(rows);
      this.dashboard = [item, ...this.dashboard];
      this.persist();

      this.options.pushItems = prevPush;
      this.options.swap = prevSwap;
      this.options.api?.optionsChanged?.();
    } else {
      this.dashboard = [...this.dashboard, { ...item }];
      this.persist();
      this.options?.api?.optionsChanged?.();
    }
  }

  private bumpDown(deltaRows: number) {
    this.dashboard = this.dashboard.map(w => ({ ...w, y: (w.y ?? 0) + deltaRows }));
  }

  getRemoveHandler(id: string): () => void { return () => this.removeWidget(id); }

  removeWidget(id: string) {
    this.dashboard = this.dashboard.filter(w => w.id !== id);
    this.persist();
  }

  clearAll() {
    if (!window.confirm('Remove all widgets from this dashboard? Saved presets will remain available.')) return;
    this.dashboard = [];
    this.selectedLayout = { UserName: this.UserName, DefaultConfig: false };
    this.persist();
  }

  private persistDebounced(ms = 80) {
    clearTimeout(this.persistTimer);
    this.persistTimer = setTimeout(() => this.persist(), ms);
  }

  persist() {
    this.normalizeLayout();
    const layout: DashboardLayout = { version: 1, items: this.dashboard, selectedLayout: this.selectedLayout ? {UserName:this.UserName,DashboardID:this.selectedLayout.DashboardID,LayoutName:this.selectedLayout.LayoutName,DefaultConfig:this.selectedLayout.DefaultConfig}:undefined };
    this.layoutSvc.save(this.userId, layout);
    this.options?.api?.optionsChanged?.();
  }

  exportLayout(): string {
    const layout: DashboardLayout = { version: 1, items: this.dashboard, selectedLayout: this.selectedLayout ? {UserName:this.UserName,DashboardID:this.selectedLayout.DashboardID,LayoutName:this.selectedLayout.LayoutName,DefaultConfig:this.selectedLayout.DefaultConfig}:undefined };
    return JSON.stringify(layout, null, 2);
  }

  importLayout(json: string) {
    try {
      const parsed = JSON.parse(json) as DashboardLayout;
      if (Array.isArray(parsed.items)) {
        this.dashboard = parsed.items;
        this.persist();
      }
    } catch (e) { console.error('Invalid layout JSON', e); }
  }

  titleFor(type: string) { return this.registry.getTitle(type); }
  componentFor(type: string) { return this.registry.getComponent(type); }

  private normalizeLayout(): void {
    this.dashboard = this.dashboard.map(w => this.clampSizeToRules(w));
  }

  clampSizeToRules(item: any): DashboardItem {
    const rules = this.registry.rules(item.type) ?? {};
    const minCols = rules.minCols ?? 1;
    const maxCols = rules.maxCols ?? this.options.maxCols ?? item.cols;
    const minRows = rules.minRows ?? 1;
    const maxRows = rules.maxRows ?? this.options.maxRows ?? item.rows;

    const cols = item.cols > maxCols || item.cols < minCols
      ? Math.max(minCols, Math.min(item.cols, maxCols))
      : item.cols;

    const rows = item.rows > maxRows || item.rows < minRows
      ? Math.max(minRows, Math.min(item.rows, maxRows))
      : item.rows;

    const gridCols = this.options.maxCols ?? cols;
    const x = Math.min(item.x ?? 0, Math.max(0, gridCols - cols));

    return { ...item, cols, rows, x };
  }

  async SaveCustomLayout() {
    const layoutName = window.prompt('What is the name for this layout?')?.trim();
    if (!layoutName) return;

    const makeDefault = await this.prompt.openPrompt({
      text: 'Make this layout the default?',
      buttons: [
        { label: 'Yes', icon: 'check', buttonClass: 'btn-yes', value: true },
        { label: 'No', icon: 'close', buttonClass: 'btn-no', value: false }
      ],
      dismissible: true
    });

    const newLayout = {
      ...this.selectedLayout,
      LayoutName: layoutName,
      DefaultConfig: !!makeDefault,
      DashboardConfig: JSON.stringify(this.layoutSvc.load(this.userId)),
      DashboardID: null
    };

    await this.savePresetLayout(JSON.stringify(newLayout));

    // IMPORTANT: do NOT call ngOnInit() here (it would rebind subscriptions if you didn’t guard)
    await this.loadLayoutOnly();
  }

  async UpdateLayout() {
    const layout = { ...this.selectedLayout, DashboardConfig: JSON.stringify(this.layoutSvc.load(this.userId)) };
    await this.savePresetLayout(JSON.stringify(layout));

    // IMPORTANT: do NOT call ngOnInit() here
    await this.loadLayoutOnly();
  }

  onChangeLayout(layout: any) {
    this.selectedLayout = layout;
    this.dashboard = JSON.parse(layout.DashboardConfig).items;
    this.persistDebounced();
  }

  async getPresetLayouts(UserName: string): Promise<any[]> {
    return await lastValueFrom(
      this.webService.getWebCall('data', '/dashboard/layout/load', {
        params: [{ name: 'UserName', value: UserName }]
      })
    );
  }

  async savePresetLayout(jsonData: string): Promise<any> {
    return await lastValueFrom(
      this.webService.getWebCall('data', '/dashboard/layout/save', { body: { jsonData } })
    );
  }

  private fallbackCopy(text: string) {
    const ta = this.doc.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    this.doc.body.appendChild(ta);
    ta.select();
    try { this.doc.execCommand('copy'); } finally { this.doc.body.removeChild(ta); }
  }

  copyLayoutToClipboard() {
    const text = this.exportLayout();
    if (navigator && 'clipboard' in navigator && (navigator as any).clipboard?.writeText) {
      (navigator as any).clipboard.writeText(text).catch(() => this.fallbackCopy(text));
    } else {
      this.fallbackCopy(text);
    }
  }

  get DefaultLayout() { return this.availableLayouts.filter((al: any) => al.DefaultConfig); }
  get CustomLayouts() { return this.availableLayouts.filter((al: any) => !al.DefaultConfig && !al.SystemCreated); }
  get SystemLayouts() { return this.availableLayouts.filter((al: any) => !al.DefaultConfig && al.SystemCreated); }
}
``
